<template>
  <div class="reward-spend-section">
    <h5 class="reward-spend-section__title">Reward Spend</h5>
    <b-row class="reward-spend-section__row">
      <b-col cols="12" lg="6">
        <div class="reward-spend-section__stats-grid">
          <app-stat
            :stat="summary?.total_granted_minor"
            :formatter="toCurrencyWholeNumber"
            description="Total granted"
          />
          <app-stat
            :stat="summary?.total_redeemed_minor"
            :formatter="toCurrencyWholeNumber"
            description="Total redeemed"
          />
          <app-stat :stat="summary?.awards_granted" description="Awards granted" />
          <app-stat :stat="summary?.user_participation" suffix="%">
            <p class="reward-spend-section__stat-description">
              User participation
              <span id="participation-info" class="reward-spend-section__info-icon">
                <font-awesome-icon :icon="['fal', 'info-circle']" />
              </span>
            </p>
            <b-tooltip target="participation-info" placement="top" variant="light">
              Share of eligible employees who've given or received recognition this period
            </b-tooltip>
          </app-stat>
        </div>
      </b-col>
      <b-col cols="12" lg="6">
        <div class="reward-spend-section__chart-card">
          <div class="reward-spend-section__chart-header">
            <h6 class="mb-0">Spend over time</h6>
            <b-form-select
              v-if="hasCategoryBreakdown"
              v-model="dimension"
              :options="DIMENSION_OPTIONS"
              size="sm"
              class="reward-spend-section__dimension"
            />
          </div>
          <bar-chart
            v-if="chartSeriesX.values.length > 0"
            :series-x="chartSeriesX"
            :series-y="chartSeriesY"
            :chart-options="chartOptions"
            :chart-dataset-options="chartDatasetOptions"
          />
          <div v-else-if="!loading" class="reward-spend-section__chart-empty">
            <p class="text-muted mb-0">No spend data available for the selected period.</p>
          </div>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import AppStat from '@/components/AppStat.vue';
import BarChart, { CHART_PRESET_OPTIONS } from '@/components/charts/BarChart.vue';
import PaletteColors from '@/styles/palette-colors.module.scss';
import { getDatasetColor } from '@/utils/chart';
import { toCurrencyWholeNumber } from '@/utils';
import { capSpendDatasets } from '@/pages/recognition/utils/analyticsTransformers';
import merge from 'lodash/merge';

const props = defineProps({
  summary: {
    type: Object,
    default: undefined,
  },
  spendOverTime: {
    type: Object,
    default: null,
  },
  spendOverTimeByCategory: {
    type: Object,
    default: null,
  },
  // Whether any program in scope actually configures spend categories. Derived from
  // program config rather than from the response, because uncategorised grants all
  // collapse into one bucket — so a company that never configured categories still
  // gets a dataset back, and would otherwise be offered a meaningless breakdown.
  hasSpendCategories: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const DIMENSION_OPTIONS = [
  { value: 'division', text: 'By division' },
  { value: 'category', text: 'By category' },
];

const dimension = ref('division');

// Offer the dimension only where categories are in use *and* there is spend to band up.
const hasCategoryBreakdown = computed(
  () => props.hasSpendCategories && (props.spendOverTimeByCategory?.datasets?.length ?? 0) > 0,
);

const activeSpendOverTime = computed(() =>
  dimension.value === 'category' && hasCategoryBreakdown.value ? props.spendOverTimeByCategory : props.spendOverTime,
);

const chartSeriesX = computed(() => ({
  values: (activeSpendOverTime.value?.labels || []).map(label => ({ name: label })),
}));

const chartSeriesY = computed(() =>
  capSpendDatasets(activeSpendOverTime.value?.datasets).map((dataset, index) => ({
    name: dataset.name,
    values: (dataset.granted_values_minor || []).map(v => v / 100),
    color: PaletteColors[getDatasetColor(index)],
  })),
);

const chartOptions = merge({}, CHART_PRESET_OPTIONS.CURRENCY, {
  scales: {
    x: { stacked: true },
    y: {
      stacked: true,
      ticks: {
        callback: label => (!isNaN(label) ? toCurrencyWholeNumber(label * 100) : label),
      },
    },
  },
  plugins: {
    legend: {
      display: true,
      position: 'bottom',
      labels: {
        usePointStyle: true,
        pointStyle: 'circle',
        padding: 20,
      },
    },
    tooltip: {
      callbacks: {
        label: context =>
          `${context.dataset.label ? `${context.dataset.label}: ` : ''}${
            !isNaN(context.parsed.y) ? toCurrencyWholeNumber(context.parsed.y * 100) : ''
          }`,
      },
    },
  },
});

const chartDatasetOptions = { borderRadius: 4, maxBarThickness: 40 };
</script>

<style scoped lang="scss">
.reward-spend-section {
  margin-bottom: 1.5rem;

  &__title {
    font-weight: 600;
    margin-bottom: 1rem;
    color: var(--palette-color-default);
  }

  &__row {
    --bs-gutter-x: 1rem;
  }

  &__stats-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 1rem;

    @media (min-width: 992px) {
      height: 100%;
      margin-bottom: 0;
    }
  }

  &__chart-card {
    background-color: var(--palette-color-base-white);
    border-radius: 0.25rem;
    box-shadow: var(--depth-2);
    padding: 1.25rem 1.5rem;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  &__chart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }

  &__dimension {
    width: auto;
    font-size: 0.875rem;
    border-color: var(--palette-color-default-lighten-80);
  }

  &__chart-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    min-height: 12.5rem;
  }

  &__stat-description {
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.3;
    margin: 0;
    color: var(--palette-color-default-lighten-30);
  }

  &__info-icon {
    cursor: pointer;
    color: var(--palette-color-default-lighten-40);
    font-size: 0.875rem;
    margin-left: 0.25rem;
  }

  :deep(.app-stat__value) {
    color: var(--palette-color-default);
  }

  :deep(.app-stat__description) {
    color: var(--palette-color-default-lighten-30);
  }

  :deep(.app-stat__subtitle) {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--palette-color-default-lighten-30);
  }
}
</style>
